import { Check, Copy } from "lucide-react"
import { motion, useReducedMotion } from "motion/react"
import { type ReactNode, useState } from "react"

/**
 * Una sección de la guía: qué es, cómo se ve y cómo se escribe.
 *
 * ⚠ **El código se puede copiar, y por eso la guía sirve.** Una referencia que hay
 * que transcribir a mano se consulta dos veces y luego se adivina; el botón de
 * copiar es lo que hace que la siguiente tabla del sistema salga igual que las
 * doce anteriores.
 *
 * ⚠ **`useReducedMotion` no es opcional aquí.** El reset de `globales.css` anula las
 * transiciones y animaciones de CSS, pero `motion` mueve los elementos escribiendo
 * estilos en cada cuadro desde JavaScript: eso ninguna regla de CSS lo detiene. Hay
 * que preguntarle a la preferencia y no animar.
 */

type Props = {
  titulo: string
  descripcion?: ReactNode
  codigo?: string
  /** Orden de aparición, para escalonar la entrada. */
  indice?: number
  children: ReactNode
}

export function Muestra({ titulo, descripcion, codigo, indice = 0, children }: Props) {
  const sinMovimiento = useReducedMotion()
  const [copiado, setCopiado] = useState(false)

  const copiar = async () => {
    if (codigo === undefined) return
    await navigator.clipboard.writeText(codigo)
    setCopiado(true)
    // Vuelve solo: un «copiado» permanente deja de significar nada al tercer uso.
    setTimeout(() => setCopiado(false), 1_500)
  }

  return (
    <motion.section
      // Entra desplazándose un poco y aclarándose. Sólo `transform` y `opacity`:
      // animar la maquetación obliga a recalcularla en cada cuadro (§7).
      initial={sinMovimiento ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.18,
        ease: [0.16, 1, 0.3, 1],
        // Escalonado: las secciones aparecen en cascada, no todas de golpe.
        delay: sinMovimiento ? 0 : Math.min(indice * 0.04, 0.32),
      }}
      className="scroll-mt-8"
      id={titulo.toLowerCase().replaceAll(" ", "-")}
    >
      <h2 className="text-lg font-semibold">{titulo}</h2>
      {descripcion !== undefined && (
        <p className="mt-1 max-w-[68ch] text-sm" style={{ color: "var(--fg-muted)" }}>
          {descripcion}
        </p>
      )}

      <div className="tarjeta mt-3 flex flex-wrap items-center gap-3">{children}</div>

      {codigo !== undefined && (
        <div className="relative mt-2">
          <pre
            className="overflow-x-auto rounded-[var(--radius-control)] border p-3 text-xs"
            style={{
              borderColor: "var(--border)",
              backgroundColor: "var(--surface-elevated)",
              fontFamily: "var(--font-mono)",
            }}
          >
            <code>{codigo}</code>
          </pre>
          <button
            type="button"
            onClick={copiar}
            aria-label={`Copiar el código de ${titulo}`}
            className="boton-secundario absolute top-2 right-2 px-2 py-1 text-xs"
            style={{ backgroundColor: "var(--surface)" }}
          >
            {copiado ? (
              <Check size={12} aria-hidden={true} />
            ) : (
              <Copy size={12} aria-hidden={true} />
            )}
            {copiado ? "Copiado" : "Copiar"}
          </button>
        </div>
      )}
    </motion.section>
  )
}
